import { useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabase";

type UserRow = {
  id: string;
  email: string;
  full_name: string | null;
  phone: string | null;
  profile_image_url: string | null;
  first_name: string | null;
  last_name: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
};

export default function AccountProfile() {
  const [uid, setUid] = useState<string | null>(null);
  const [u, setU] = useState<UserRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [newPwd, setNewPwd] = useState("");
  const [newPwd2, setNewPwd2] = useState("");

  // Load / ensure a row exists in public.users for the signed-in user
  useEffect(() => {
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      const id = auth.user?.id ?? null;
      if (!id) return;
      setUid(id);

      // try fetch
      const { data, error } = await supabase
        .from("users")
        .select("*")
        .eq("id", id)
        .maybeSingle();

      if (error) { setErr(error.message); return; }

      if (data) {
        setU(data as UserRow);
      } else {
        // upsert a minimal row (id must equal auth.uid())
        const { data: ins, error: insErr } = await supabase
          .from("users")
          .insert({ id, email: auth.user?.email ?? "" })
          .select()
          .single();
        if (insErr) { setErr(insErr.message); return; }
        setU(ins as UserRow);
      }
    })();
  }, []);

  const initials = useMemo(() => {
    const i1 = u?.first_name?.[0] ?? u?.full_name?.[0] ?? u?.email?.[0] ?? "";
    const i2 = u?.last_name?.[0] ?? "";
    const combo = (i1 + i2).trim();
    return (combo || "U").toUpperCase();
  }, [u?.first_name, u?.last_name, u?.full_name, u?.email]);

  async function uploadAvatar(file: File) {
    if (!uid) return;
    const ext = (file.name.split(".").pop() || "png").toLowerCase();
    const path = `${uid}/${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from("avatars").upload(path, file, { upsert: true });
    if (error) throw error;
    const { data: pub } = supabase.storage.from("avatars").getPublicUrl(path);
    return pub.publicUrl;
  }

  async function onAvatarChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f || !u) return;
    setMsg(null); setErr(null);
    try {
      const url = await uploadAvatar(f);
      const { data, error } = await supabase
        .from("users")
        .update({ profile_image_url: url })
        .eq("id", u.id)
        .select()
        .single();
      if (error) throw error;
      setU(data as UserRow);
      setMsg("Profile photo updated.");
    } catch (e: any) {
      setErr(e.message ?? "Upload failed");
    }
  }

  async function saveProfile() {
    if (!u) return;
    setSaving(true); setErr(null); setMsg(null);
    try {
      const payload = {
        first_name: u.first_name,
        last_name: u.last_name,
        full_name: u.full_name,   // keep in sync if you want
        phone: u.phone,
        address1: u.address1,
        address2: u.address2,
        city: u.city,
        state: u.state,
        zip: u.zip,
      };
      const { data, error } = await supabase
        .from("users")
        .update(payload)
        .eq("id", u.id)
        .select()
        .single();
      if (error) throw error;
      setU(data as UserRow);
      setMsg("Saved!");
    } catch (e: any) {
      setErr(e.message ?? "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function changePassword() {
    setErr(null); setMsg(null);
    if (!newPwd || newPwd !== newPwd2) {
      setErr("Passwords don’t match.");
      return;
    }
    const { error } = await supabase.auth.updateUser({ password: newPwd });
    if (error) { setErr(error.message); return; }
    setNewPwd(""); setNewPwd2("");
    setMsg("Password updated.");
  }

  if (!u) return <div style={{ padding: 24 }}>Loading…</div>;

  return (
    <div style={{ padding: 24 }}>
      <h1 style={{ marginTop: 0 }}>Profile</h1>

      {msg && <Banner kind="ok">{msg}</Banner>}
      {err && <Banner kind="err">{err}</Banner>}

      <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 16, alignItems: "start" }}>
        {/* Avatar */}
        <div>
          {u.profile_image_url ? (
            <img src={u.profile_image_url} width={120} height={120} style={{ borderRadius: "50%", objectFit: "cover" }} />
          ) : (
            <div style={{
              width: 120, height: 120, borderRadius: "50%", background: "#E5E7EB",
              display: "grid", placeItems: "center", fontSize: 36, fontWeight: 800, color: "#374151"
            }}>
              {initials}
            </div>
          )}
          <div style={{ marginTop: 10 }}>
            <label style={{ fontSize: 14 }}>
              <input type="file" accept="image/*" onChange={onAvatarChange} style={{ display: "none" }} />
              <span style={linkBtn}>Upload photo</span>
            </label>
          </div>
        </div>

        {/* Form */}
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="First name">
              <input value={u.first_name ?? ""} onChange={e => setU({ ...u, first_name: e.target.value })} style={input} />
            </Field>
            <Field label="Last name">
              <input value={u.last_name ?? ""} onChange={e => setU({ ...u, last_name: e.target.value })} style={input} />
            </Field>

            <Field label="Full name (optional display)">
              <input value={u.full_name ?? ""} onChange={e => setU({ ...u, full_name: e.target.value })} style={input} />
            </Field>
            <Field label="Phone">
              <input value={u.phone ?? ""} onChange={e => setU({ ...u, phone: e.target.value })} style={input} />
            </Field>

            <Field label="Address line 1">
              <input value={u.address1 ?? ""} onChange={e => setU({ ...u, address1: e.target.value })} style={input} />
            </Field>
            <Field label="Address line 2">
              <input value={u.address2 ?? ""} onChange={e => setU({ ...u, address2: e.target.value })} style={input} />
            </Field>
            <Field label="City">
              <input value={u.city ?? ""} onChange={e => setU({ ...u, city: e.target.value })} style={input} />
            </Field>
            <Field label="State">
              <input value={u.state ?? ""} onChange={e => setU({ ...u, state: e.target.value })} style={input} />
            </Field>
            <Field label="ZIP">
              <input value={u.zip ?? ""} onChange={e => setU({ ...u, zip: e.target.value })} style={input} />
            </Field>
          </div>

          <div style={{ marginTop: 12 }}>
            <button onClick={saveProfile} disabled={saving} style={primaryBtn}>
              {saving ? "Saving…" : "Save Profile"}
            </button>
          </div>
        </div>
      </div>

      {/* Password */}
      <div style={{ marginTop: 20, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 16 }}>
        <h3 style={{ marginTop: 0 }}>Change Password</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="New password">
            <input type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)} style={input} />
          </Field>
          <Field label="Confirm new password">
            <input type="password" value={newPwd2} onChange={e => setNewPwd2(e.target.value)} style={input} />
          </Field>
        </div>
        <div style={{ marginTop: 12 }}>
          <button onClick={changePassword} style={secondaryBtn}>Update Password</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "grid", gap: 6 }}>
      <span style={{ fontSize: 13, color: "#374151" }}>{label}</span>
      {children}
    </label>
  );
}

function Banner({ kind, children }: { kind: "ok" | "err"; children: React.ReactNode }) {
  return (
    <div style={{
      marginBottom: 12,
      padding: "10px 12px",
      borderRadius: 10,
      background: kind === "ok" ? "#ECFDF5" : "#FEF2F2",
      border: `1px solid ${kind === "ok" ? "#A7F3D0" : "#FCA5A5"}`,
      color: kind === "ok" ? "#065F46" : "#991B1B"
    }}>{children}</div>
  );
}

const input: React.CSSProperties = { padding: "10px 12px", borderRadius: 10, border: "1px solid #e5e7eb", outline: "none" };
const primaryBtn: React.CSSProperties = { padding: "10px 12px", borderRadius: 10, border: "none", background: "#0B2E5B", color: "white", fontWeight: 700, cursor: "pointer" };
const secondaryBtn: React.CSSProperties = { padding: "10px 12px", borderRadius: 10, border: "1px solid #0B2E5B", background: "white", color: "#0B2E5B", fontWeight: 700, cursor: "pointer" };
const linkBtn: React.CSSProperties = { padding: "6px 10px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", cursor: "pointer" };
